import { FormEvent, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Address } from "~/types/address";
import { ReduxState } from "~/types/redux";
import useAddressCheck from "../useAddressCheck";
import Cookie from "js-cookie";
import { setCookie } from "~/helpers/cookie";
import { resetAddressRoute } from "~/redux/actions/address";
import { useRouter } from "next/router";
import { useBuyFlowDecision } from "../useBuyFlowDecision";
import useEnrichCustomer from "../useEnrichCustomer";
import { useResumeQuoteCheck } from "../useResumeQuoteCheck";
import { ModalKeys } from "~/constants/modal";

interface ReturnType {
  apiVersion?: string;
  selectedAddress: Address;
  buttonState: { disabled: boolean; loading: boolean };
  addressCheckCompleted: boolean;
  setSelectedAddress: (address: Address) => void;
  handleAddressFormSubmit: (event: FormEvent<HTMLFormElement>) => Promise<void>;
  handleInputChange: (event: FormEvent<HTMLInputElement>) => void;
  resetAddress: () => void;
}

/**
 * Orchestrates the logic on the /buy page.
 *   - Retrieves the buy flow (NGC or BAU)
 *   - Handles the address check form submission
 */
export const useBuy = (): ReturnType => {
  const dispatch = useDispatch();
  const { address, enrichedCustomer } = useSelector((store: ReduxState) => store);
  const { activeModal } = useResumeQuoteCheck();
  const { runEnrichment } = useEnrichCustomer();
  const [addressCheckCompleted, setAddressCheckCompleted] = useState(false);
  const router = useRouter();
  const { "opt-in": optIn } = router.query;

  useBuyFlowDecision({ addressCheckCompleted });

  const resetAddress = () => {
    dispatch(resetAddressRoute());
    Cookie.remove("moversSwitchers");
    setAddressCheckCompleted(false);
    setSelectedAddress({
      addressKey: "",
      address: "",
      address2: "",
      city: "",
      state: "",
      zip: "",
      parsedAddress: {},
    });
  };

  useEffect(() => {
    if (optIn === "true") {
      setCookie("ngcOptin", optIn, { otGroup: "C0003" });
    }
  }, [optIn]);

  const {
    apiVersion,
    selectedAddress,
    setSelectedAddress,
    handleFormSubmit,
    handleInputChange,
    buttonState,
    submitted,
  } = useAddressCheck();

  useEffect(() => {
    if (address?.install?.address) {
      setAddressCheckCompleted(true);

      if (activeModal === ModalKeys.RESUME) return;

      const hasEnrichmentData = enrichedCustomer && Object.keys(enrichedCustomer).length > 0;

      if (!hasEnrichmentData) {
        runEnrichment(address?.install);
      }
    }
  }, [address?.install?.address, submitted]);

  const handleAddressFormSubmit = async (event: FormEvent<HTMLFormElement>) => {
    handleFormSubmit(event);
    setAddressCheckCompleted(true);
  };

  return {
    apiVersion,
    selectedAddress,
    buttonState,
    addressCheckCompleted,
    setSelectedAddress,
    handleAddressFormSubmit,
    handleInputChange,
    resetAddress,
  };
};
